import { useState } from 'react'

interface Props {
  token: string
  apiBase: string
  downloadUrl: string
  /**
   * The page's primary action is elsewhere ("Open this desk in your browser"):
   * this becomes the quieter second way to keep the desk, in an account.
   */
  secondary?: boolean
}

// The "View + add to my workspace" flow. Renders below a scope=copy
// snapshot. Three states:
//
//   - idle:    intro card with "Add to my workspace" button
//   - form:    email + password to sign up (or log in if account exists)
//   - success: "we've put this in your inbox" + Download Plexii CTA
//
// On signup, we call POST /accounts/signup → get a session token →
// POST /inbox/add with { token, sessionToken } → the share is now waiting
// for them in Plexii. They install the desktop app, sign in with the
// same email + password, and the share automatically appears in their
// "Shared with me" sidebar.
//
// Honest scope: no email verification, no password reset, no MFA in v1.
// Production deployment should wire those when the user base justifies
// the complexity.

type Mode = 'idle' | 'form' | 'success' | 'login'

export default function AddToWorkspace({
  token,
  apiBase,
  downloadUrl,
  secondary = false
}: Props): JSX.Element {
  const [mode, setMode] = useState<Mode>('idle')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [handle, setHandle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSignup(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      // Sign up — server returns a session token.
      const signupRes = await fetch(`${apiBase}/accounts/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          handle: handle.trim() || null
        })
      })
      if (!signupRes.ok) {
        const j = await signupRes.json().catch(() => ({}))
        if (signupRes.status === 409) {
          setError('An account with that email already exists. Try logging in.')
          setMode('login')
        } else {
          setError(j.error || `Signup failed (${signupRes.status}).`)
        }
        return
      }
      const signupJson = (await signupRes.json()) as { sessionToken?: string; error?: string }
      const sessionToken = signupJson.sessionToken
      if (!sessionToken) {
        setError('Signup succeeded but no session was returned. Try again.')
        return
      }
      // Add the share to the new account's inbox.
      await addToInbox(sessionToken)
      setMode('success')
    } catch (err) {
      setError(`Could not sign up: ${(err as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleLogin(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const loginRes = await fetch(`${apiBase}/accounts/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password
        })
      })
      if (!loginRes.ok) {
        const j = await loginRes.json().catch(() => ({}))
        setError(j.error || `Login failed (${loginRes.status}).`)
        return
      }
      const loginJson = (await loginRes.json()) as { sessionToken?: string; error?: string }
      const sessionToken = loginJson.sessionToken
      if (!sessionToken) {
        setError('Login succeeded but no session was returned. Try again.')
        return
      }
      await addToInbox(sessionToken)
      setMode('success')
    } catch (err) {
      setError(`Could not log in: ${(err as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  async function addToInbox(sessionToken: string): Promise<void> {
    const res = await fetch(`${apiBase}/inbox/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionToken}`
      },
      body: JSON.stringify({ shareToken: token })
    })
    if (!res.ok) {
      const j = await res.json().catch(() => ({}))
      throw new Error(j.error || `Could not add to inbox (${res.status}).`)
    }
  }

  if (mode === 'success') {
    return (
      <div className="mt-8 glass-card rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <div
            className="h-10 w-10 rounded-xl inline-flex items-center justify-center text-[18px] shrink-0"
            style={{
              background:
                'linear-gradient(135deg, rgba(52, 211, 153, 0.25), rgba(52, 211, 153, 0.10))',
              color: 'rgb(167, 243, 208)'
            }}
          >
            ✓
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-stone-100 mb-1">
              Added to your workspace
            </h3>
            <p className="text-[13px] text-stone-300 leading-relaxed mb-4">
              We've put this in your <strong className="text-stone-100">Shared with me</strong> inbox.
              Install Plexii and sign in with{' '}
              <span className="font-mono text-stone-200">{email}</span> — it'll
              be waiting for you.
            </p>
            <a
              href={downloadUrl}
              className="btn-primary inline-block"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Plexii
            </a>
          </div>
        </div>
      </div>
    )
  }

  if (mode === 'form' || mode === 'login') {
    const isLogin = mode === 'login'
    return (
      <div className="mt-8 glass-card rounded-2xl p-6">
        <h3 className="text-base font-semibold text-stone-100 mb-1">
          {isLogin ? 'Log in to your Plexii account' : 'Create your Plexii account'}
        </h3>
        <p className="text-[12px] text-stone-400 mb-4">
          We'll add this share to your inbox right away. After signup, install
          Plexii and sign in to see it.
        </p>
        <form
          onSubmit={isLogin ? handleLogin : handleSignup}
          className="space-y-3"
        >
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="w-full px-3 py-2 rounded-md text-stone-100 placeholder:text-stone-500"
              style={{
                background: 'rgba(0,0,0,0.32)',
                border: '1px solid rgba(255,255,255,0.08)'
              }}
              placeholder="you@example.com"
            />
          </div>
          {!isLogin && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">
                Display handle <span className="text-stone-500 normal-case font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="what your shares will say"
                className="w-full px-3 py-2 rounded-md text-stone-100 placeholder:text-stone-500"
                style={{
                  background: 'rgba(0,0,0,0.32)',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
                maxLength={32}
              />
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-400 font-semibold mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 rounded-md text-stone-100 placeholder:text-stone-500"
              style={{
                background: 'rgba(0,0,0,0.32)',
                border: '1px solid rgba(255,255,255,0.08)'
              }}
              placeholder={isLogin ? 'your password' : 'at least 8 characters'}
            />
          </div>
          {error && (
            <div
              className="text-[12px] px-3 py-2 rounded-md"
              style={{
                background: 'rgba(244,114,182,0.10)',
                border: '1px solid rgba(244,114,182,0.25)',
                color: 'rgb(251, 207, 232)'
              }}
            >
              {error}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 pt-1">
            <button
              type="button"
              onClick={() => setMode(isLogin ? 'form' : 'login')}
              className="text-[12px] text-stone-400 hover:text-stone-100 transition-colors"
            >
              {isLogin ? 'Need an account? Sign up' : 'Already have one? Log in'}
            </button>
            <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60">
              {busy
                ? 'Working…'
                : isLogin
                  ? 'Log in & add'
                  : 'Sign up & add'}
            </button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="mt-8 glass-card rounded-2xl p-6">
      <div className="flex items-start gap-3">
        <div
          className="h-10 w-10 rounded-xl inline-flex items-center justify-center text-[18px] shrink-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(99, 102, 241, 0.18))',
            color: 'white'
          }}
        >
          +
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-stone-100 mb-1">
            {secondary ? 'Or keep it in a Plexii account' : 'Want this in your own workspace?'}
          </h3>
          <p className="text-[13px] text-stone-400 leading-relaxed mb-4">
            Create a free Plexii account and we'll drop this into your{' '}
            <strong className="text-stone-100">Shared with me</strong> inbox.
            Once you install Plexii, sign in and it's there — ready to
            duplicate into your own desk.
          </p>
          <button onClick={() => setMode('form')} className={secondary ? 'btn-ghost' : 'btn-primary'}>
            Add to my workspace
          </button>
        </div>
      </div>
    </div>
  )
}
